import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Trophy, TrendingUp, Users, Search, X } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useTheme } from "../context/ThemeContext";
import RotatingText from "../components/common/RotatingText";
import CustomSelect from "../components/common/CustomSelect";
import { sanitizeInput } from "../utils/inputSanitization";

const API_BASE = "https://api.fsgu.co.za";

// Colors for top 5 players
const PLAYER_COLORS = [
  "#22c55e", // green - 1st
  "#3b82f6", // blue - 2nd
  "#f59e0b", // amber - 3rd
  "#ec4899", // pink - 4th
  "#8b5cf6", // purple - 5th
];

const CATEGORIES = [
  { value: "Mens_Open", label: "Men's Open" },
  { value: "Mens_Mid_Am", label: "Men's Mid-Am" },
  { value: "Mens_Senior", label: "Men's Senior" },
  { value: "Mens_Super_Senior", label: "Men's Super Senior" },
  { value: "Ladies_Open", label: "Ladies Open" },
  { value: "Ladies_Mid_Am", label: "Ladies Mid-Am" },
  { value: "Ladies_Senior", label: "Ladies Senior" },
  { value: "Ladies_Super_Senior", label: "Ladies Super Senior" },
  { value: "Junior_Boys", label: "Junior Boys" },
  { value: "Junior_Girls", label: "Junior Girls" },
  { value: "Mixed", label: "Mixed" },
  { value: "Country_Districts", label: "Country Districts" },
];

const getCategoryLabel = (category) => {
  return CATEGORIES.find((c) => c.value === category)?.label || category;
};

const OrderOfMeritPage = () => {
  const { isDarkMode } = useTheme();
  const [allSeasons, setAllSeasons] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [standings, setStandings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [standingsLoading, setStandingsLoading] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Initial fetch - get all seasons once
  useEffect(() => {
    const init = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/oom-seasons.php?status=active`,
        );
        const data = await response.json();

        if (data.success && data.data?.length > 0) {
          setAllSeasons(data.data);

          const years = [...new Set(data.data.map((s) => s.year))].sort(
            (a, b) => b - a,
          );
          setAvailableYears(years);

          const latestYear = years[0];
          setSelectedYear(latestYear);

          const yearSeasons = data.data.filter((s) => s.year === latestYear);
          setSeasons(yearSeasons);

          if (yearSeasons.length > 0) {
            setSelectedSeason(yearSeasons[0].id);
          } else {
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching seasons:", error);
        setLoading(false);
      }
    };
    init();
  }, []);

  // Handle year changes
  const handleYearChange = useCallback(
    (year) => {
      setSelectedYear(year);
      setSearchQuery("");

      const yearSeasons = allSeasons.filter((s) => s.year === year);
      setSeasons(yearSeasons);

      if (yearSeasons.length > 0) {
        setSelectedSeason(yearSeasons[0].id);
      } else {
        setSelectedSeason(null);
        setStandings(null);
      }
    },
    [allSeasons],
  );

  // Fetch standings when season changes
  useEffect(() => {
    if (!selectedSeason) return;

    const fetchStandings = async () => {
      try {
        setStandingsLoading(true);
        const response = await fetch(
          `${API_BASE}/oom-standings.php?season_id=${selectedSeason}`,
        );
        const data = await response.json();

        if (data.success) {
          setStandings(data);
        }
      } catch (error) {
        console.error("Error fetching standings:", error);
      } finally {
        setLoading(false);
        setStandingsLoading(false);
      }
    };

    fetchStandings();
  }, [selectedSeason]);

  // Memoized derived values
  const hasData = useMemo(() => standings?.standings?.length > 0, [standings]);
  const hasEvents = useMemo(() => standings?.events?.length > 0, [standings]);
  const top5Players = useMemo(
    () => standings?.standings?.slice(0, 5) || [],
    [standings],
  );

  // Chart data
  const chartData = useMemo(() => {
    if (!hasData || !hasEvents) return [];

    const top5 = standings.standings.slice(0, 5);
    const regularEvents = standings.events
      .filter((e) => !e.is_bonus_event)
      .sort((a, b) => a.event_number - b.event_number);

    if (regularEvents.length === 0) return [];

    return regularEvents.map((event, eventIndex) => {
      const dataPoint = {
        event: `${event.event_number}`,
        eventName: event.name || `Event ${event.event_number}`,
      };

      top5.forEach((player, playerIndex) => {
        let cumulative = 0;
        for (let i = 0; i <= eventIndex; i++) {
          const scoreKey = `e_${regularEvents[i].event_number}`;
          cumulative += player.event_scores?.[scoreKey] || 0;
        }
        dataPoint[`player${playerIndex}`] = cumulative;
        dataPoint[`player${playerIndex}Name`] = player.player_name;
      });

      return dataPoint;
    });
  }, [standings, hasData, hasEvents]);

  // Filtered standings
  const filteredStandings = useMemo(() => {
    if (!hasData) return [];
    if (!searchQuery) return standings.standings;

    const query = searchQuery.toLowerCase();
    return standings.standings.filter((player) =>
      player.player_name.toLowerCase().includes(query),
    );
  }, [standings, searchQuery, hasData]);

  // Event counts
  const eventCounts = useMemo(() => {
    if (!hasEvents) return { regular: 0, bonus: 0 };
    return {
      regular: standings.events.filter((e) => !e.is_bonus_event).length,
      bonus: standings.events.filter((e) => e.is_bonus_event).length,
    };
  }, [standings, hasEvents]);

  // Event score key helper
  const getEventScoreKey = useCallback((event) => {
    if (event.is_bonus_event) {
      return `bp_${event.event_number - 100}`;
    }
    return `e_${event.event_number}`;
  }, []);

  // Event display label helper
  const getEventLabel = useCallback((event) => {
    if (event.is_bonus_event) {
      return `BP${event.event_number - 100}`;
    }
    return event.event_number;
  }, []);

  const handleSeasonChange = useCallback((seasonId) => {
    setSelectedSeason(seasonId);
    setSearchQuery("");
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(sanitizeInput(e.target.value, "text"));
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery("");
  }, []);

  const isLoading = loading || standingsLoading;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] md:min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/oomHero.png"
            alt="Order of Merit"
            className="w-full h-full object-cover"
          />
          <div
            className={`absolute inset-0 ${
              isDarkMode
                ? "bg-gradient-to-b from-fsgu-gray-900/60 via-fsgu-gray-900/70 to-fsgu-gray-900/90"
                : "bg-gradient-to-b from-black/40 via-black/50 to-black/70"
            }`}
          ></div>
        </div>

        <div className="container-custom px-4 relative z-10 text-center">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold font-cursive mb-8 md:mb-12 text-white leading-tight tracking-tight">
              Order of Merit
            </h1>

            <div className="flex flex-wrap items-center justify-center gap-3 text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-light mb-8">
              <span className="drop-shadow-lg text-white">Track</span>
              <RotatingText
                texts={["Excellence", "Performance", "Rankings", "Champions"]}
                mainClassName={`font-cursive font-normal px-6 py-3 pb-4 rounded-2xl ${
                  isDarkMode
                    ? "bg-fsgu-green-700/95 text-white"
                    : "bg-fsgu-green-700 text-white"
                } drop-shadow-2xl`}
                splitLevelClassName="overflow-visible"
                staggerFrom="first"
                initial={{ y: "100%", opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: "-120%", opacity: 0 }}
                staggerDuration={0.03}
                transition={{ type: "spring", damping: 30, stiffness: 400 }}
                rotationInterval={3000}
              />
            </div>

            <p className="text-lg sm:text-xl md:text-2xl text-white/90 font-light max-w-3xl mx-auto leading-relaxed">
              Season rankings and player standings
            </p>
          </div>
        </div>

        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-10 hidden md:block">
          <div className="animate-bounce">
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </div>
        </div>
      </section>

      {/* Standings Section */}
      <section className="section">
        <div className="container-custom">
          <div className="text-center mb-8">
            <h2
              className={`text-2xl font-semibold mb-2 ${
                isDarkMode ? "text-fsgu-silver-200" : "text-fsgu-silver-800"
              }`}
            >
              Current Standings
            </h2>
            <p
              className={`text-sm max-w-2xl mx-auto ${
                isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"
              }`}
            >
              Points accumulated from FSGU events and selected bonus tournaments
            </p>
          </div>

          {/* Filters */}
          {(availableYears.length > 0 || seasons.length > 0) && (
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              {availableYears.length > 0 && (
                <div className="w-full sm:w-48">
                  <CustomSelect
                    name="year"
                    value={selectedYear}
                    onChange={(e) => handleYearChange(Number(e.target.value))}
                    options={availableYears.map((year) => ({
                      value: year,
                      label: `${year} Season`,
                    }))}
                    placeholder="Select Year"
                  />
                </div>
              )}

              {seasons.length > 0 && (
                <div className="w-full sm:w-64">
                  <CustomSelect
                    name="season"
                    value={selectedSeason || ""}
                    onChange={(e) => handleSeasonChange(Number(e.target.value))}
                    options={seasons.map((season) => ({
                      value: season.id,
                      label: getCategoryLabel(season.category),
                    }))}
                    placeholder="Select Category"
                  />
                </div>
              )}
            </div>
          )}

          {/* Content */}
          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-fsgu-green-500 mb-4"></div>
              <p
                className={
                  isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"
                }
              >
                Loading standings...
              </p>
            </div>
          ) : !hasData ? (
            <div
              className={`text-center py-16 rounded-xl border ${
                isDarkMode
                  ? "bg-fsgu-gray-800 border-fsgu-gray-700"
                  : "bg-white border-fsgu-silver-200"
              }`}
            >
              <div
                className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6 ${
                  isDarkMode ? "bg-fsgu-green-500/20" : "bg-fsgu-green-500/10"
                }`}
              >
                <TrendingUp
                  className={`w-8 h-8 ${
                    isDarkMode ? "text-fsgu-green-400" : "text-fsgu-green-500"
                  }`}
                />
              </div>
              <h3
                className={`text-xl font-semibold mb-2 ${
                  isDarkMode ? "text-fsgu-silver-300" : "text-fsgu-silver-700"
                }`}
              >
                Coming Soon
              </h3>
              <p
                className={`max-w-md mx-auto ${
                  isDarkMode ? "text-fsgu-silver-500" : "text-fsgu-silver-500"
                }`}
              >
                Order of Merit standings will be available once events have been
                played and scores recorded.
              </p>
            </div>
          ) : (
            <div
              className={`rounded-xl border overflow-hidden ${
                isDarkMode
                  ? "bg-fsgu-gray-800 border-fsgu-gray-700"
                  : "bg-white border-fsgu-silver-200"
              }`}
            >
              {/* Season Info */}
              <div
                className={`p-4 border-b ${
                  isDarkMode ? "border-fsgu-gray-700" : "border-fsgu-silver-200"
                }`}
              >
                <h3
                  className={`text-lg font-medium mb-1 ${
                    isDarkMode ? "text-fsgu-silver-200" : "text-fsgu-silver-800"
                  }`}
                >
                  {standings.season?.name ||
                    getCategoryLabel(standings.season?.category)}
                </h3>
                <p
                  className={`flex items-center gap-4 text-sm ${
                    isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"
                  }`}
                >
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {standings.standings.length} players
                  </span>
                  {hasEvents && (
                    <span className="flex items-center gap-1">
                      <Trophy className="w-4 h-4" />
                      {eventCounts.regular} events
                      {eventCounts.bonus > 0 && (
                        <span className="text-fsgu-gold-500 ml-1">
                          +{eventCounts.bonus} bonus
                        </span>
                      )}
                    </span>
                  )}
                </p>
              </div>

              {/* Top 5 Progression Chart */}
              {chartData.length > 1 && top5Players.length > 0 && (
                <div
                  className={`p-6 border-b ${
                    isDarkMode
                      ? "bg-fsgu-gray-900/50 border-fsgu-gray-700"
                      : "bg-fsgu-silver-50 border-fsgu-silver-200"
                  }`}
                >
                  <h4
                    className={`text-lg font-semibold mb-4 ${
                      isDarkMode
                        ? "text-fsgu-silver-200"
                        : "text-fsgu-silver-800"
                    }`}
                  >
                    Top 5 Race to the Top
                  </h4>

                  <div className="flex flex-wrap gap-4 mb-4">
                    {top5Players.map((player, index) => (
                      <div
                        key={player.player_id || index}
                        className="flex items-center gap-2"
                      >
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: PLAYER_COLORS[index] }}
                        />
                        <span
                          className={`text-sm ${
                            isDarkMode
                              ? "text-fsgu-silver-300"
                              : "text-fsgu-silver-700"
                          }`}
                        >
                          {player.player_name}
                        </span>
                        <span
                          className={`text-sm font-bold ${
                            isDarkMode
                              ? "text-fsgu-silver-400"
                              : "text-fsgu-silver-500"
                          }`}
                        >
                          ({player.best_10_points || player.total_points})
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="h-64 sm:h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={chartData}
                        margin={{ top: 5, right: 10, left: -10, bottom: 5 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={isDarkMode ? "#374151" : "#e5e7eb"}
                        />
                        <XAxis
                          dataKey="event"
                          stroke={isDarkMode ? "#9ca3af" : "#6b7280"}
                          fontSize={12}
                          tickMargin={5}
                        />
                        <YAxis
                          stroke={isDarkMode ? "#9ca3af" : "#6b7280"}
                          fontSize={11}
                          width={38}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                            border: `1px solid ${isDarkMode ? "#374151" : "#e5e7eb"}`,
                            borderRadius: "8px",
                          }}
                          labelStyle={{
                            color: isDarkMode ? "#f3f4f6" : "#111827",
                          }}
                          formatter={(value, name, props) => {
                            const playerIndex = parseInt(
                              name.replace("player", ""),
                            );
                            const playerName =
                              props.payload[`player${playerIndex}Name`];
                            return [value, playerName];
                          }}
                        />
                        {top5Players.map((_, index) => (
                          <Line
                            key={index}
                            type="monotone"
                            dataKey={`player${index}`}
                            stroke={PLAYER_COLORS[index]}
                            strokeWidth={2}
                            dot={{
                              fill: PLAYER_COLORS[index],
                              strokeWidth: 0,
                              r: 4,
                            }}
                            activeDot={{ r: 6, strokeWidth: 0 }}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Full Standings Table */}
              <div className="overflow-x-auto">
                {/* Search */}
                <div
                  className={`px-4 py-3 border-b ${
                    isDarkMode
                      ? "border-fsgu-gray-700"
                      : "border-fsgu-silver-200"
                  }`}
                >
                  <div className="relative max-w-xs">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search
                        className={`w-4 h-4 ${
                          isDarkMode
                            ? "text-fsgu-silver-500"
                            : "text-fsgu-silver-400"
                        }`}
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="Search player..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      className={`w-full pl-10 pr-10 py-2 rounded-lg text-sm ${
                        isDarkMode
                          ? "bg-fsgu-gray-700 text-fsgu-silver-100 placeholder-fsgu-silver-500 focus:ring-fsgu-green-500/30"
                          : "bg-fsgu-silver-100 text-fsgu-silver-900 placeholder-fsgu-silver-500 focus:ring-fsgu-green-500/30"
                      } focus:outline-none focus:ring-2`}
                    />
                    {searchQuery && (
                      <button
                        onClick={clearSearch}
                        className={`absolute inset-y-0 right-0 pr-3 flex items-center ${
                          isDarkMode
                            ? "text-fsgu-silver-500 hover:text-fsgu-silver-300"
                            : "text-fsgu-silver-400 hover:text-fsgu-silver-600"
                        }`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {searchQuery && (
                    <p
                      className={`text-xs mt-2 ${isDarkMode ? "text-fsgu-silver-500" : "text-fsgu-silver-500"}`}
                    >
                      Found {filteredStandings.length} player
                      {filteredStandings.length !== 1 ? "s" : ""}
                    </p>
                  )}
                </div>

                <table className="w-full">
                  <thead>
                    <tr
                      className={
                        isDarkMode ? "bg-fsgu-gray-900/50" : "bg-fsgu-silver-50"
                      }
                    >
                      <th
                        className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
                          isDarkMode
                            ? "text-fsgu-silver-400"
                            : "text-fsgu-silver-600"
                        }`}
                      >
                        Rank
                      </th>
                      <th
                        className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
                          isDarkMode
                            ? "text-fsgu-silver-400"
                            : "text-fsgu-silver-600"
                        }`}
                      >
                        Player
                      </th>
                      {hasEvents &&
                        standings.events.map((event) => (
                          <th
                            key={event.id}
                            className={`px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider whitespace-nowrap hidden lg:table-cell ${
                              event.is_bonus_event
                                ? isDarkMode
                                  ? "text-fsgu-gold-400"
                                  : "text-fsgu-gold-600"
                                : isDarkMode
                                  ? "text-fsgu-silver-400"
                                  : "text-fsgu-silver-600"
                            }`}
                            title={
                              event.name || `Event ${event.event_number}`
                            }
                          >
                            {getEventLabel(event)}
                          </th>
                        ))}
                      <th
                        className={`px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider ${
                          isDarkMode
                            ? "text-fsgu-green-400"
                            : "text-fsgu-green-600"
                        }`}
                      >
                        Total
                      </th>
                      <th
                        className={`px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider ${
                          isDarkMode
                            ? "text-fsgu-gold-400"
                            : "text-fsgu-gold-600"
                        }`}
                        title="10 Best FS Rounds + Bonus Points"
                      >
                        Best 10
                      </th>
                      <th
                        className={`px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider hidden sm:table-cell ${
                          isDarkMode
                            ? "text-fsgu-silver-400"
                            : "text-fsgu-silver-600"
                        }`}
                      >
                        Events
                      </th>
                    </tr>
                  </thead>
                  <tbody
                    className={`divide-y ${
                      isDarkMode
                        ? "divide-fsgu-gray-700"
                        : "divide-fsgu-silver-200"
                    }`}
                  >
                    {filteredStandings.map((player, index) => (
                      <tr
                        key={player.player_id || index}
                        className={
                          isDarkMode
                            ? "hover:bg-fsgu-gray-700/50"
                            : "hover:bg-fsgu-silver-50"
                        }
                      >
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-semibold ${
                              player.rank === 1
                                ? "bg-fsgu-gold-500 text-white"
                                : player.rank === 2
                                  ? "bg-fsgu-silver-400 text-white"
                                  : player.rank === 3
                                    ? "bg-amber-600 text-white"
                                    : isDarkMode
                                      ? "bg-fsgu-gray-700 text-fsgu-silver-400"
                                      : "bg-fsgu-silver-200 text-fsgu-silver-600"
                            }`}
                          >
                            {player.rank}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div
                            className={`font-medium ${
                              isDarkMode
                                ? "text-fsgu-silver-100"
                                : "text-fsgu-silver-900"
                            }`}
                          >
                            {player.player_name}
                          </div>
                          {player.club_name && (
                            <div
                              className={`text-xs ${
                                isDarkMode
                                  ? "text-fsgu-silver-500"
                                  : "text-fsgu-silver-500"
                              }`}
                            >
                              {player.club_name}
                            </div>
                          )}
                        </td>
                        {hasEvents &&
                          standings.events.map((event) => {
                            const scoreKey = getEventScoreKey(event);
                            const score = player.event_scores?.[scoreKey];

                            return (
                              <td
                                key={event.id}
                                className={`px-2 py-3 text-center text-sm hidden lg:table-cell ${
                                  score !== null && score !== undefined
                                    ? event.is_bonus_event
                                      ? isDarkMode
                                        ? "text-fsgu-gold-400"
                                        : "text-fsgu-gold-600"
                                      : isDarkMode
                                        ? "text-fsgu-silver-200"
                                        : "text-fsgu-silver-800"
                                    : isDarkMode
                                      ? "text-fsgu-silver-600"
                                      : "text-fsgu-silver-400"
                                }`}
                              >
                                {score ?? "-"}
                              </td>
                            );
                          })}
                        <td
                          className={`px-4 py-3 text-center font-bold ${
                            isDarkMode
                              ? "text-fsgu-green-400"
                              : "text-fsgu-green-600"
                          }`}
                        >
                          {player.total_points}
                        </td>
                        <td
                          className={`px-4 py-3 text-center font-bold ${
                            isDarkMode
                              ? "text-fsgu-gold-400"
                              : "text-fsgu-gold-600"
                          }`}
                        >
                          {player.best_10_points || '-'}
                        </td>
                        <td
                          className={`px-4 py-3 text-center hidden sm:table-cell ${
                            isDarkMode
                              ? "text-fsgu-silver-400"
                              : "text-fsgu-silver-600"
                          }`}
                        >
                          {player.events_played}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div
                className={`px-6 py-4 border-t ${
                  isDarkMode ? "border-fsgu-gray-700" : "border-fsgu-silver-200"
                }`}
              >
                <p
                  className={`text-sm ${
                    isDarkMode ? "text-fsgu-silver-500" : "text-fsgu-silver-500"
                  }`}
                >
                  {standings.season?.year} Season
                  {hasEvents && ` • ${eventCounts.regular} events completed`}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Category Quick Links */}
      {seasons.length > 1 && (
        <section
          className={`section ${
            isDarkMode ? "bg-fsgu-gray-800/50" : "bg-fsgu-silver-50"
          }`}
        >
          <div className="container-custom">
            <h2
              className={`text-2xl font-bold text-center mb-8 ${
                isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
              }`}
            >
              All Categories
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {seasons.map((season) => (
                <button
                  key={season.id}
                  onClick={() => {
                    setSelectedSeason(season.id);
                    window.scrollTo({ top: 600, behavior: "smooth" });
                  }}
                  className={`card-hover group cursor-pointer text-left ${
                    selectedSeason === season.id
                      ? isDarkMode
                        ? "ring-2 ring-fsgu-green-500"
                        : "ring-2 ring-fsgu-green-500"
                      : ""
                  }`}
                >
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
                      isDarkMode
                        ? "bg-fsgu-green-500/20"
                        : "bg-fsgu-green-500/10"
                    }`}
                  >
                    <Trophy
                      className={`w-5 h-5 ${
                        isDarkMode
                          ? "text-fsgu-green-400"
                          : "text-fsgu-green-500"
                      }`}
                    />
                  </div>
                  <h3
                    className={`font-semibold mb-1 ${
                      isDarkMode
                        ? "text-fsgu-silver-100"
                        : "text-fsgu-silver-900"
                    }`}
                  >
                    {getCategoryLabel(season.category)}
                  </h3>
                  <p
                    className={`text-sm ${
                      isDarkMode
                        ? "text-fsgu-silver-400"
                        : "text-fsgu-silver-600"
                    }`}
                  >
                    {season.player_count || 0} players
                  </p>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default OrderOfMeritPage;